import { DataLogStatus } from 'store/app/enums';
import { MappedReturn } from 'store/returns/models/returnData';

/**
 * Filter configuration for navigation from homepage cards to submissions page
 */
export interface FilterConfig {
  type: string;
  value?: string;
  statusFilter?: string;
  excludeStatuses?: DataLogStatus[]; // Statuses to always exclude
  personFilter?: string; // Person filter (format: "role:personName" or "preparer:name,reviewer:name")
}

/**
 * Get all open statuses (not submitted or cancelled)
 */
export const getOpenStatuses = (): string => {
  const allStatuses = Object.values(DataLogStatus);
  const openStatuses = allStatuses.filter(
    (status) => status !== DataLogStatus.SUBMITTED && status !== DataLogStatus.CANCELLED
  );
  return openStatuses.join(',');
};

/**
 * Always exclude these statuses from filters
 */
export const DEFAULT_EXCLUDED_STATUSES = [
  DataLogStatus.SUBMITTED,
  DataLogStatus.CANCELLED,
];

/**
 * Filter returns to exclude specified statuses
 */
export const excludeStatuses = (
  items: MappedReturn[],
  excludedStatuses: DataLogStatus[] = DEFAULT_EXCLUDED_STATUSES
): MappedReturn[] => {
  return items.filter(
    (item) => !excludedStatuses.includes(item.status as DataLogStatus)
  );
};

/**
 * Apply status filter from string (comma-separated)
 */
export const applyStatusFilter = (
  items: MappedReturn[],
  statusFilterString?: string
): MappedReturn[] => {
  if (!statusFilterString) return items;
  
  const statusArray = statusFilterString.split(',').map(s => s.trim());
  return items.filter((item) => statusArray.includes(item.status));
};

/**
 * Combine status filter with exclusion - ensures excluded statuses are always removed
 */
export const applyStatusFilterWithExclusion = (
  items: MappedReturn[],
  statusFilterString?: string,
  excludedStatuses: DataLogStatus[] = DEFAULT_EXCLUDED_STATUSES
): MappedReturn[] => {
  // First apply status filter if provided
  let filtered = statusFilterString 
    ? applyStatusFilter(items, statusFilterString)
    : items;
  
  // Then always exclude specified statuses
  return excludeStatuses(filtered, excludedStatuses);
};

/**
 * Build filter config for navigation
 */
export const buildFilterConfig = (
  type: string,
  value?: string,
  options?: {
    includeOpenStatuses?: boolean;
    excludeStatuses?: DataLogStatus[];
    personFilter?: string; // Person filter (format: "role:personName")
  }
): FilterConfig => {
  const config: FilterConfig = {
    type,
    value,
  };

  if (options?.includeOpenStatuses) {
    config.statusFilter = getOpenStatuses();
  }

  if (options?.excludeStatuses && options.excludeStatuses.length > 0) {
    config.excludeStatuses = options.excludeStatuses;
  }

  if (options?.personFilter) {
    config.personFilter = options.personFilter;
  }

  return config;
};

/**
 * Convert filter config to URL search params
 */
export const filterConfigToSearchParams = (config: FilterConfig): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('filter', config.type);
  
  if (config.value) {
    params.set('value', config.value);
  }
  
  if (config.statusFilter) {
    params.set('statusFilter', config.statusFilter);
  }
  
  if (config.personFilter) {
    params.set('personFilter', config.personFilter);
  }
  
  return params;
};
